import React from "react"
import type { ColumnsType } from "antd/es/table"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faFolder, faFile } from "@fortawesome/free-solid-svg-icons"

import DataQButton from "shared/ui/Buttons/DataQButton.component"
import type { DocumentItem, FileItem, FolderItem } from "types/document.types"

/**
 * Function to generate columns for the document table with folder support.
 *
 * @param deleteProjectDocument - Function to delete a document or folder.
 * @param downloadProjectDocument - Function to download a document.
 * @param onFolderClick - Function to handle folder navigation.
 * @returns The array of column objects.
 */
const getColumns = (
  deleteProjectDocument: (
    fullPath: string,
    isFolder?: boolean
  ) => Promise<void>,
  downloadProjectDocument: (filename: string) => Promise<void>,
  onFolderClick: (folderName: string) => void
): ColumnsType<DocumentItem> => [
  {
    title: "Name",
    dataIndex: "name",
    key: "name",
    render: (name: string, record: DocumentItem) => (
      <div
        className={`flex items-center ${
          record.type === "folder" ? "cursor-pointer hover:text-blue-600" : ""
        }`}
        onClick={() => {
          if (record.type === "folder") {
            onFolderClick(record.name)
          }
        }}
      >
        <FontAwesomeIcon
          icon={record.type === "folder" ? faFolder : faFile}
          className={`mr-2 ${
            record.type === "folder" ? "text-[#36c3ed]" : "text-[#36c3ed]"
          }`}
        />
        <span className="font-medium text-gray-900">{name}</span>
        {record.type === "folder" && (
          <span className="ml-2 text-sm text-gray-500">(folder)</span>
        )}
      </div>
    ),
    sorter: (a: DocumentItem, b: DocumentItem) => {
      // Sort folders first, then files, both alphabetically
      if (a.type === "folder" && b.type === "file") return -1
      if (a.type === "file" && b.type === "folder") return 1
      return a.name.localeCompare(b.name)
    },
    sortDirections: ["ascend", "descend"],
  },
  {
    title: "Type",
    key: "type",
    width: 100,
    render: (_value: undefined, record: DocumentItem) => (
      <span
        className={`px-2 py-1 rounded text-xs font-medium ${
          record.type === "folder"
            ? "bg-blue-100 text-blue-800"
            : "bg-gray-100 text-gray-800"
        }`}
      >
        {record.type === "folder" ? "Folder" : "File"}
      </span>
    ),
    filters: [
      { text: "Folder", value: "folder" },
      { text: "File", value: "file" },
    ],
    onFilter: (value: boolean | React.Key, record: DocumentItem) =>
      record.type === value,
  },
  {
    title: <div style={{ textAlign: "left" }}>Actions</div>,
    key: "actions",
    align: "right",
    width: 200,
    render: (_value: undefined, record: DocumentItem) => (
      <div className="flex justify-end space-x-2">
        {record.type === "file" ? (
          // File actions
          <>
            <DataQButton
              type="primary"
              onClick={() =>
                downloadProjectDocument((record as FileItem).fullPath)
              }
            >
              Download
            </DataQButton>
            <DataQButton
              type="danger"
              onClick={() =>
                deleteProjectDocument((record as FileItem).fullPath, false)
              }
            >
              Delete
            </DataQButton>
          </>
        ) : (
          // Folder actions
          <>
            <DataQButton
              type="primary"
              onClick={() => onFolderClick(record.name)}
            >
              Open
            </DataQButton>
            <DataQButton
              type="danger"
              onClick={() =>
                deleteProjectDocument((record as FolderItem).fullPath, true)
              }
            >
              Delete
            </DataQButton>
          </>
        )}
      </div>
    ),
  },
]

export default getColumns
